/**
 * Custom Filters - Tilray CA Theme
 * Handles custom filter UI interactions including:
 * - Filter button toggle
 * - Floating panel management
 * - Price range slider
 * - Active filter chips
 */

import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const DESKTOP_FILTER_QUERY = '(min-width: 750px)';

/**
 * @param {HTMLElement | null | undefined} panel
 */
function isMobileSortPanel(panel) {
  return panel?.classList.contains('custom-filter-panel--mobile-sort') ?? false;
}

/**
 * Mobile sort uses a detached bottom sheet and needs the dimmed overlay.
 * Desktop sort is anchored in the bar — a full-screen overlay sits above it and blocks clicks.
 */
function shouldUseSortOverlay(panel) {
  return isMobileSortPanel(panel);
}

let suppressFilterOutsideClose = false;

/** @type {string | null} */
let persistOpenFilterPanelId = null;

/**
 * @param {() => void} callback
 */
function whileSuppressingFilterOutsideClose(callback) {
  suppressFilterOutsideClose = true;
  try {
    callback();
  } finally {
    queueMicrotask(() => {
      suppressFilterOutsideClose = false;
    });
  }
}

/**
 * Keep overlay visibility in sync with what's actually open in the DOM.
 */
function syncOverlayVisibility() {
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const anyFilterPanelOpen = Boolean(
    document.querySelector('.custom-filter-panels > .custom-filter-panel--open:not(.custom-filter-panel--mobile-sort)')
  );
  const anySortOpen = Boolean(document.querySelector('.custom-sort-dropdown details[open]'));
  const anyMobileSortPanelOpen = Boolean(
    document.querySelector('.custom-filter-panel--mobile-sort.custom-filter-panel--open')
  );
  const anyFilterNeedsOverlay = anyFilterPanelOpen;
  const anySortNeedsOverlay = isMobile && (anySortOpen || anyMobileSortPanelOpen);

  if (anyFilterNeedsOverlay) {
    OverlayManager.show('filters', { lockScroll: false });
  } else {
    OverlayManager.hide('filters');
  }

  if (anySortNeedsOverlay) {
    OverlayManager.show('sort', { lockScroll: false });
  } else {
    OverlayManager.hide('sort');
  }

  if (!anyFilterNeedsOverlay && !anySortNeedsOverlay) {
    OverlayManager.hide();
  }
}

/** @type {number | null} */
let repositionFrame = null;

function rememberOpenQuickFilterPanel() {
  const openButton = Array.from(document.querySelectorAll('custom-filter-button')).find(
    (button) => button.isOpen
  );

  persistOpenFilterPanelId =
    openButton?.querySelector('[aria-controls]')?.getAttribute('aria-controls') ?? null;
}

/**
 * @param {string | null} panelId
 */
function syncQuickFilterPanelOpenState(panelId) {
  if (!panelId) return;

  document.querySelectorAll('custom-filter-button').forEach((button) => {
    const controlsId = button.querySelector('[aria-controls]')?.getAttribute('aria-controls');
    const panel = resolveFilterPanel(button);
    const trigger = button.querySelector('.custom-filter-btn');
    const shouldBeOpen = controlsId === panelId;

    button.isOpen = shouldBeOpen;
    trigger?.setAttribute('aria-expanded', shouldBeOpen ? 'true' : 'false');

    if (!panel) return;

    if (shouldBeOpen) {
      panel.classList.add('custom-filter-panel--open');
      if (trigger) positionFilterPanel(panel, trigger);
    } else {
      panel.classList.remove('custom-filter-panel--open');
      panel.removeAttribute('data-open-trigger-id');
      resetFilterPanelPosition(panel);
    }
  });
}

/**
 * Move any stray portaled panels back into the panels container.
 */
function repatriateQuickFilterPanels() {
  document.querySelectorAll('custom-filter-button .custom-filter-panel').forEach((panel) => {
    const button = panel.closest('custom-filter-button');
    if (!button) return;

    const sectionId = button.closest('custom-filter-bar')?.dataset.sectionId;
    const home =
      document.querySelector(`.custom-filter-panels[data-section-id="${sectionId}"]`) ??
      document.querySelector('.custom-filter-panels');

    if (home && !home.contains(panel)) {
      home.appendChild(panel);
    }
  });
}

/**
 * @param {string} name
 * @param {string} value
 * @param {boolean} checked
 */
function syncDrawerFilterInput(name, value, checked) {
  if (!name) return;

  document
    .querySelectorAll(`.custom-filters-drawer__form input[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`)
    .forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = checked;
      }
    });
}

/**
 * Keep only the active viewport's sort radios enabled for form submission.
 */
function syncSortInputDisabledState() {
  const isMobile = window.matchMedia('(max-width: 749px)').matches;

  document.querySelectorAll('input[data-sort-input="desktop"]').forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.disabled = isMobile;
    }
  });

  document.querySelectorAll('input[data-sort-input="mobile"]').forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.disabled = !isMobile;
    }
  });
}

/**
 * @param {string} value
 */
function syncSortRadios(value) {
  if (!value) return;

  document.querySelectorAll('input[name="sort_by"]').forEach((input) => {
    if (input instanceof HTMLInputElement) {
      input.checked = input.value === value;
    }
  });

  document.querySelectorAll('.custom-sort-option').forEach((option) => {
    const input = option.querySelector('input[name="sort_by"]');
    const isSelected = input instanceof HTMLInputElement && input.value === value;

    option.classList.toggle('custom-sort-option--selected', isSelected);

    if (isSelected) {
      const labelText = option.querySelector(':scope > span:not(.custom-sort-option__check)')?.textContent?.trim();
      if (labelText) {
        document.querySelectorAll('.custom-sort-btn__value').forEach((el) => {
          el.textContent = labelText;
        });
      }
    }
  });
}

/**
 * Align detached quick-filter panel checkboxes with the current URL.
 */
function syncQuickPanelsFromURL() {
  const url = new URL(window.location.href);

  document.querySelectorAll('.custom-filter-panels .custom-filter-grid__input').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;

    const activeValues = url.searchParams.getAll(input.name);
    input.checked = activeValues.includes(input.value);

    const item = input.closest('custom-filter-grid-item');
    if (item) {
      item.classList.toggle('custom-filter-grid__item--selected', input.checked);
      item.setAttribute('aria-checked', String(input.checked));
    }
  });

  const sortBy = url.searchParams.get('sort_by');
  if (sortBy) {
    syncSortRadios(sortBy);
  }

  syncFilterButtonStatesFromURL();
  syncDrawerFromURL();
}

/**
 * Reflect active filters on quick-filter pill buttons.
 */
function syncFilterButtonStatesFromURL() {
  const url = new URL(window.location.href);

  document.querySelectorAll('custom-filter-button').forEach((button) => {
    const panelId = button.querySelector('[aria-controls]')?.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    const trigger = button.querySelector('.custom-filter-btn');
    if (!trigger) return;

    let hasActive = false;

    if (panel) {
      const priceGte = panel.querySelector('input[name="filter.v.price.gte"]');
      const priceLte = panel.querySelector('input[name="filter.v.price.lte"]');

      if (priceGte || priceLte) {
        hasActive = Boolean(
          url.searchParams.get('filter.v.price.gte') || url.searchParams.get('filter.v.price.lte')
        );
      } else {
        const paramNames = new Set();
        panel.querySelectorAll('.custom-filter-grid__input').forEach((input) => {
          if (input instanceof HTMLInputElement && input.name) {
            paramNames.add(input.name);
          }
        });

        for (const paramName of paramNames) {
          const activeValues = url.searchParams.getAll(paramName);
          if (activeValues.length > 0) {
            hasActive = true;
            break;
          }
        }
      }
    }

    trigger.classList.toggle('custom-filter-btn--active', hasActive);
    trigger.classList.toggle('custom-filter-btn--inactive', !hasActive);
  });
}

/**
 * Keep drawer filter inputs aligned with the current URL.
 */
function syncDrawerFromURL() {
  const url = new URL(window.location.href);

  document.querySelectorAll('.custom-filters-drawer__form input[type="checkbox"]').forEach((input) => {
    if (!(input instanceof HTMLInputElement) || !input.name) return;

    const activeValues = url.searchParams.getAll(input.name);
    input.checked = activeValues.includes(input.value);

    const item = input.closest('.custom-drawer-filter__item');
    if (item) {
      item.classList.toggle('custom-drawer-filter__item--selected', input.checked);
    }
  });
}

/** Close any open quick-filter panels after section updates. */
function resetOpenFilterPanels() {
  repatriateQuickFilterPanels();

  document.querySelectorAll('custom-filter-button').forEach((button) => {
    button.isOpen = false;
    button.querySelector('.custom-filter-btn')?.setAttribute('aria-expanded', 'false');
  });

  document
    .querySelectorAll('.custom-filter-panels > .custom-filter-panel:not(.custom-filter-panel--mobile-sort)')
    .forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      panel.classList.remove('custom-filter-panel--open');
      panel.removeAttribute('data-open-trigger-id');
      resetFilterPanelPosition(panel);
    });

  syncOverlayVisibility();
}

function isQuickFilterPanel(panel) {
  if (!(panel instanceof HTMLElement)) return false;
  return panel.id.startsWith('panel-') || Boolean(panel.closest('.custom-filter-panels'));
}

/**
 * @param {CustomFilterButton} button
 * @returns {HTMLElement | null}
 */
function resolveFilterPanel(button) {
  const controlsId = button.querySelector('[aria-controls]')?.getAttribute('aria-controls');
  if (controlsId) {
    button.panel = document.getElementById(controlsId);
  }

  return button.panel instanceof HTMLElement ? button.panel : null;
}

/**
 * @returns {FacetsFormComponent | null}
 */
function getDesktopFacetsForm() {
  const bar = document.querySelector('custom-filter-bar');
  const form = bar?.closest('facets-form-component');
  return form instanceof HTMLElement && 'updateFilters' in form ? /** @type {FacetsFormComponent} */ (form) : null;
}

/**
 * @param {HTMLElement | null | undefined} panel
 */
function isDetachedFilterPanel(panel) {
  return isQuickFilterPanel(panel);
}

/**
 * @param {HTMLElement | null | undefined} panel
 * @param {HTMLElement | null | undefined} trigger
 */
function positionFilterPanel(panel, trigger) {
  if (!panel || !trigger || !window.matchMedia(DESKTOP_FILTER_QUERY).matches) return;
  if (!isQuickFilterPanel(panel)) return;

  const rect = trigger.getBoundingClientRect();
  const panelWidth = panel.offsetWidth || 420;
  let left = rect.right - panelWidth;

  left = Math.max(16, left);
  left = Math.min(left, window.innerWidth - panelWidth - 16);

  panel.style.position = 'fixed';
  panel.style.top = `${rect.bottom + 10}px`;
  panel.style.left = `${left}px`;
  panel.style.right = 'auto';
  panel.style.bottom = 'auto';
  panel.style.width = '';
  panel.style.maxWidth = '';
  panel.style.transform = '';
}

/**
 * Reposition open desktop panels (scroll/resize).
 */
function repositionOpenFilterPanels() {
  if (!window.matchMedia(DESKTOP_FILTER_QUERY).matches) return;

  document.querySelectorAll('custom-filter-button').forEach((button) => {
    if (!button.isOpen) return;

    const panel = resolveFilterPanel(button);
    const trigger = button.querySelector('.custom-filter-btn');
    if (panel && trigger) {
      positionFilterPanel(panel, trigger);
    }
  });
}

function scheduleRepositionOpenFilterPanels() {
  if (!window.matchMedia(DESKTOP_FILTER_QUERY).matches) return;
  if (repositionFrame) return;

  repositionFrame = requestAnimationFrame(() => {
    repositionFrame = null;
    repositionOpenFilterPanels();
  });
}

/**
 * @param {HTMLElement | null | undefined} panel
 */
function resetFilterPanelPosition(panel) {
  if (!panel || !isQuickFilterPanel(panel)) return;

  panel.style.position = '';
  panel.style.top = '';
  panel.style.left = '';
  panel.style.right = '';
  panel.style.bottom = '';
  panel.style.width = '';
  panel.style.maxWidth = '';
  panel.style.transform = '';
}

/**
 * Shared overlay manager - prevents duplicate event handlers
 */
const OverlayManager = {
  /** @type {HTMLDivElement | null} */
  overlay: null,
  initialized: false,
  overlayOwners: new Set(),
  scrollLockOwners: new Set(),

  getOverlay() {
    if (!this.overlay) {
      this.overlay = /** @type {HTMLDivElement | null} */ (document.querySelector('.custom-filter-overlay'));
      if (!this.overlay) {
        this.overlay = document.createElement('div');
        this.overlay.className = 'custom-filter-overlay';
        document.body.appendChild(this.overlay);
      }
    }
    return this.overlay;
  },

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.getOverlay().addEventListener('click', () => {
      this.closeAll();
    });
  },

  update() {
    const overlay = this.getOverlay();
    const hasVisibleOwners = this.overlayOwners.size > 0;
    const hasScrollLockOwners = this.scrollLockOwners.size > 0;

    overlay.classList.toggle('custom-filter-overlay--visible', hasVisibleOwners);
    document.body.style.overflow = hasScrollLockOwners ? 'hidden' : '';
  },

  /**
   * @param {string} owner
   * @param {{ lockScroll?: boolean }} [options]
   */
  show(owner, { lockScroll = true } = {}) {
    if (!owner) return;

    this.overlayOwners.add(owner);

    if (lockScroll) {
      this.scrollLockOwners.add(owner);
    } else {
      this.scrollLockOwners.delete(owner);
    }

    this.update();
  },

  /**
   * @param {string} [owner]
   */
  hide(owner) {
    if (owner) {
      this.overlayOwners.delete(owner);
      this.scrollLockOwners.delete(owner);
    } else {
      this.overlayOwners.clear();
      this.scrollLockOwners.clear();
    }

    this.update();
  },

  closeAll() {
    document.querySelectorAll('custom-filter-button').forEach((btn) => {
      btn.isOpen = false;
      const panel = resolveFilterPanel(btn);
      panel?.classList.remove('custom-filter-panel--open');
      panel?.removeAttribute('data-open-trigger-id');
      resetFilterPanelPosition(panel);
      btn.querySelector('.custom-filter-btn')?.setAttribute('aria-expanded', 'false');
    });

    repatriateQuickFilterPanels();
    closeSortUI();
    syncOverlayVisibility();
  }
};

/**
 * Custom Filter Button Component
 * Handles the filter button that opens a floating panel
 */
class CustomFilterButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    resolveFilterPanel(this);
    this.trigger = this.querySelector('.custom-filter-btn');
    this.isOpen = false;

    OverlayManager.init();

    this.boundHandleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.boundHandleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.boundHandleKeydown);
  }

  updatedCallback() {
    super.updatedCallback();
    resolveFilterPanel(this);
  }

  togglePanel() {
    if (this.isOpen) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  openPanel() {
    closeSortUI();

    const panel = resolveFilterPanel(this);
    if (!panel || !this.trigger) return;

    document.querySelectorAll('custom-filter-button').forEach((other) => {
      if (other === this || !other.isOpen) return;

      other.isOpen = false;
      const otherPanel = resolveFilterPanel(other);
      otherPanel?.classList.remove('custom-filter-panel--open');
      otherPanel?.removeAttribute('data-open-trigger-id');
      resetFilterPanelPosition(otherPanel);
      other.querySelector('.custom-filter-btn')?.setAttribute('aria-expanded', 'false');
    });

    this.isOpen = true;
    panel.dataset.openTriggerId = this.id || '';
    positionFilterPanel(panel, this.trigger);
    panel.classList.add('custom-filter-panel--open');
    this.trigger.setAttribute('aria-expanded', 'true');
    syncOverlayVisibility();
  }

  closePanel() {
    const panel = resolveFilterPanel(this);
    this.isOpen = false;
    panel?.classList.remove('custom-filter-panel--open');
    panel?.removeAttribute('data-open-trigger-id');
    resetFilterPanelPosition(panel);
    this.trigger?.setAttribute('aria-expanded', 'false');
    syncOverlayVisibility();
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.closePanel();
    }
  }
}

if (!customElements.get('custom-filter-button')) {
  customElements.define('custom-filter-button', CustomFilterButton);
}

/**
 * Custom Price Range Slider Component
 * Implements a dual-handle range slider for price filtering
 */
class CustomPriceSlider extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    this.minInput = this.querySelector('.custom-range-slider__input--min');
    this.maxInput = this.querySelector('.custom-range-slider__input--max');
    this.fillElement = this.querySelector('.custom-range-slider__fill');
    this.minLabel = this.querySelector('.custom-price-slider__label--min');
    this.maxLabel = this.querySelector('.custom-price-slider__label--max');
    
    this.minValue = parseFloat(this.dataset.min) || 0;
    this.maxValue = parseFloat(this.dataset.max) || 1000;
    this.currentMin = parseFloat(this.dataset.currentMin) || this.minValue;
    this.currentMax = parseFloat(this.dataset.currentMax) || this.maxValue;
    this.currency = this.dataset.currency || '$';
    
    this.initSliders();
    this.updateUI();
  }

  initSliders() {
    if (this.minInput) {
      this.minInput.min = this.minValue;
      this.minInput.max = this.maxValue;
      this.minInput.value = this.currentMin;
      this.minInput.addEventListener('input', this.handleMinChange.bind(this));
      this.minInput.addEventListener('change', this.handleChangeComplete.bind(this));
    }

    if (this.maxInput) {
      this.maxInput.min = this.minValue;
      this.maxInput.max = this.maxValue;
      this.maxInput.value = this.currentMax;
      this.maxInput.addEventListener('input', this.handleMaxChange.bind(this));
      this.maxInput.addEventListener('change', this.handleChangeComplete.bind(this));
    }
  }

  handleMinChange(event) {
    const value = parseFloat(event.target.value);
    if (value <= this.currentMax) {
      this.currentMin = value;
      this.updateUI();
    } else {
      event.target.value = this.currentMax;
    }
  }

  handleMaxChange(event) {
    const value = parseFloat(event.target.value);
    if (value >= this.currentMin) {
      this.currentMax = value;
      this.updateUI();
    } else {
      event.target.value = this.currentMin;
    }
  }

  handleChangeComplete = debounce(() => {
    // Update hidden inputs for form submission
    const minHiddenInput = this.querySelector('input[name*="price.gte"]');
    const maxHiddenInput = this.querySelector('input[name*="price.lte"]');
    
    if (minHiddenInput) {
      minHiddenInput.value = this.currentMin > this.minValue ? this.currentMin : '';
    }
    if (maxHiddenInput) {
      maxHiddenInput.value = this.currentMax < this.maxValue ? this.currentMax : '';
    }

    // Close the panel and overlay before triggering the filter update
    OverlayManager.closeAll();

    // Trigger form update
    const facetsForm = this.closest('facets-form-component');
    if (facetsForm && typeof facetsForm.updateFilters === 'function') {
      facetsForm.updateFilters();
    }
  }, 300);

  updateUI() {
    const range = this.maxValue - this.minValue;
    const minPercent = ((this.currentMin - this.minValue) / range) * 100;
    const maxPercent = ((this.currentMax - this.minValue) / range) * 100;

    if (this.fillElement) {
      this.fillElement.style.left = `${minPercent}%`;
      this.fillElement.style.width = `${maxPercent - minPercent}%`;
    }

    if (this.minLabel) {
      this.minLabel.textContent = this.formatPrice(this.currentMin);
    }

    if (this.maxLabel) {
      this.maxLabel.textContent = this.formatPrice(this.currentMax);
    }
  }

  formatPrice(value) {
    return `${this.currency}${value.toFixed(2)}`;
  }
}

if (!customElements.get('custom-price-slider')) {
  customElements.define('custom-price-slider', CustomPriceSlider);
}

/**
 * Custom Filter Grid Item Component
 * Handles the grid items in the filter panel with icon + checkbox layout
 */
class CustomFilterGridItem extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.checkbox = this.querySelector('.custom-filter-grid__input');
    this.checkbox?.addEventListener('change', this.handleCheckboxChange.bind(this));
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleCheckboxChange() {
    this.updateState();
    this.applyFilterSelection();
  }

  handleClick(event) {
    if (event.target === this.checkbox) return;

    if (this.classList.contains('custom-filter-grid__item--disabled')) {
      event.preventDefault();
      return;
    }

    event.stopPropagation();

    if (this.checkbox) {
      this.checkbox.checked = !this.checkbox.checked;
      this.updateState();
      this.applyFilterSelection();
    }
  }

  handleKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.handleClick(event);
    }
  }

  updateState() {
    if (this.checkbox?.checked) {
      this.classList.add('custom-filter-grid__item--selected');
    } else {
      this.classList.remove('custom-filter-grid__item--selected');
    }
  }

  applyFilterSelection() {
    if (!this.checkbox) return;

    syncDrawerFilterInput(this.checkbox.name, this.checkbox.value, this.checkbox.checked);

    const facetsForm =
      this.closest('facet-inputs-component')?.closest('facets-form-component') ?? getDesktopFacetsForm();

    if (!(facetsForm && typeof facetsForm.updateFilters === 'function')) return;

    rememberOpenQuickFilterPanel();

    whileSuppressingFilterOutsideClose(() => {
      facetsForm.updateFilters();
    });
  }
}

if (!customElements.get('custom-filter-grid-item')) {
  customElements.define('custom-filter-grid-item', CustomFilterGridItem);
}

/**
 * Custom Filter Chip Component
 * Handles the active filter chip with remove functionality
 */
class CustomFilterChip extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.removeButton = this.querySelector('.custom-filter-chip__close');
    this.removeButton?.addEventListener('click', this.handleRemove.bind(this));
  }

  handleRemove(event) {
    event.stopPropagation();
    const url = this.dataset.removeUrl;
    
    if (url) {
      // Add immediate visual feedback - fade out the chip
      this.style.opacity = '0.5';
      this.style.pointerEvents = 'none';
      
      const facetsForm = this.closest('facets-form-component');
      if (facetsForm && typeof facetsForm.updateFiltersByURL === 'function') {
        facetsForm.updateFiltersByURL(url);
      } else {
        window.location.href = url;
      }
    }
  }
}

if (!customElements.get('custom-filter-chip')) {
  customElements.define('custom-filter-chip', CustomFilterChip);
}

/**
 * Custom Filter Bar Component
 */
class CustomFilterBar extends Component {}

if (!customElements.get('custom-filter-bar')) {
  customElements.define('custom-filter-bar', CustomFilterBar);
}

/**
 * @param {HTMLDetailsElement} details
 * @returns {HTMLElement | null}
 */
function getSortPanel(details) {
  if (!(details instanceof HTMLDetailsElement)) return null;

  const panelId = details.dataset.sortPanelId;
  if (panelId) {
    const panel = document.getElementById(panelId);
    if (panel instanceof HTMLElement) return panel;
  }

  const panel = details.querySelector('.custom-filter-panel');
  return panel instanceof HTMLElement ? panel : null;
}

/**
 * @returns {boolean}
 */
function isSortUIOpen() {
  return Boolean(
    document.querySelector('.custom-sort-dropdown details[open]') ||
      document.querySelector('.custom-filter-panel--mobile-sort.custom-filter-panel--open') ||
      document.querySelector('.custom-sort-dropdown--desktop .custom-filter-panel--open')
  );
}

/**
 * @param {EventTarget | null} target
 * @returns {boolean}
 */
function isSortInteractionTarget(target) {
  if (!(target instanceof Element)) return false;
  if (target.closest('.custom-filter-panel--mobile-sort')) return true;
  if (target.closest('.custom-sort-dropdown')) return true;
  return false;
}

/** Close all sort panels and sync overlay state. */
function closeSortUI() {
  document.querySelectorAll('.custom-sort-dropdown details[open]').forEach((details) => {
    details.removeAttribute('open');
  });

  document
    .querySelectorAll('.custom-filter-panel--mobile-sort.custom-filter-panel--open, .custom-sort-dropdown .custom-filter-panel--open')
    .forEach((panel) => {
      if (panel instanceof HTMLElement) {
        panel.classList.remove('custom-filter-panel--open');
        resetFilterPanelPosition(panel);
      }
    });

  syncOverlayVisibility();
}

/**
 * Sort dropdown behavior: match filter panel (overlay + slide-up/open class)
 */
function syncSortDropdownState(details) {
  if (!(details instanceof HTMLDetailsElement)) return;
  if (!details.closest('.custom-sort-dropdown')) return;

  const panel = getSortPanel(details);
  if (!panel) return;

  if (details.open) {
    document.querySelectorAll('custom-filter-button').forEach((btn) => {
      btn.isOpen = false;
      const panel = resolveFilterPanel(btn);
      panel?.classList.remove('custom-filter-panel--open');
      panel?.removeAttribute('data-open-trigger-id');
      resetFilterPanelPosition(panel);
      btn.querySelector('.custom-filter-btn')?.setAttribute('aria-expanded', 'false');
    });

    if (shouldUseSortOverlay(panel)) {
      OverlayManager.show('sort', { lockScroll: false });
    }

    panel.classList.add('custom-filter-panel--open');
    syncOverlayVisibility();
  } else {
    panel.classList.remove('custom-filter-panel--open');
    resetFilterPanelPosition(panel);
    syncOverlayVisibility();
  }
}

// Primary hook for details state changes
document.addEventListener('toggle', (event) => {
  syncSortDropdownState(event.target);
});

// Fallback: ensure overlay syncs on summary click in all browsers
document.addEventListener('click', (event) => {
  const summary = event.target.closest('.custom-sort-dropdown summary');
  if (!summary) return;

  const details = summary.closest('details');
  if (!details) return;

  const willOpen = !details.open;
  const panel = getSortPanel(details);
  if (!panel) return;

  setTimeout(() => {
    if (!details.open) {
      syncSortDropdownState(details);
      return;
    }

    if (willOpen) {
      if (shouldUseSortOverlay(panel)) {
        OverlayManager.show('sort', { lockScroll: false });
      }
      panel.classList.add('custom-filter-panel--open');
      syncOverlayVisibility();
      return;
    }

    syncSortDropdownState(details);
  }, 16);
});

/**
 * Select All functionality for filter panels
 */
document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  if (!event.target.closest('.custom-filter-panel__select-all')) return;

  event.preventDefault();
  event.stopPropagation();

  const panel = event.target.closest('.custom-filter-panel');
  const facetInputs = panel?.querySelector('facet-inputs-component');
  const items = panel?.querySelectorAll('.custom-filter-grid__item:not(.custom-filter-grid__item--disabled)');
  let changed = false;

  items?.forEach((item) => {
    const checkbox = item.querySelector('.custom-filter-grid__input');
    if (!(checkbox instanceof HTMLInputElement) || checkbox.checked || checkbox.disabled) return;

    checkbox.checked = true;
    item.classList.add('custom-filter-grid__item--selected');
    syncDrawerFilterInput(checkbox.name, checkbox.value, true);
    changed = true;
  });

  if (!changed) return;

  const facetsForm = panel?.closest('facets-form-component') ?? getDesktopFacetsForm();
  if (facetsForm && typeof facetsForm.updateFilters === 'function') {
    rememberOpenQuickFilterPanel();

    whileSuppressingFilterOutsideClose(() => {
      facetsForm.updateFilters();
    });
  }
});

/**
 * Handle sort option selection - close the dropdown after selection
 */
document.addEventListener('change', (event) => {
  if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'sort_by') return;

  const sortPanel = event.target.closest('.custom-filter-panel');
  const isMobileSort = sortPanel?.classList.contains('custom-filter-panel--mobile-sort') ?? false;
  const isDesktopSort = Boolean(event.target.closest('.custom-sort-dropdown'));

  if (!isMobileSort && !isDesktopSort) return;

  syncSortInputDisabledState();
  syncSortRadios(event.target.value);

  const facetsForm =
    event.target.closest('facets-form-component') ??
    event.target.closest('form')?.closest('facets-form-component');

  closeSortUI();

  if (facetsForm && typeof facetsForm.updateFilters === 'function') {
    facetsForm.updateFilters(event.target.value);
  }
});

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  if (event.target.classList.contains('custom-filter-overlay')) return;
  if (suppressFilterOutsideClose) return;

  queueMicrotask(() => {
    if (suppressFilterOutsideClose) return;

    const openButton = Array.from(document.querySelectorAll('custom-filter-button')).find(
      (button) => button.isOpen
    );
    if (!openButton) return;

    const path = event.composedPath();
    const panel = resolveFilterPanel(openButton);
    const panelsContainer = document.querySelector('.custom-filter-panels');

    if (path.includes(openButton)) return;
    if (panel && path.includes(panel)) return;
    if (panelsContainer && path.includes(panelsContainer)) return;

    openButton.closePanel();
  });
});

document.addEventListener('click', (event) => {
  if (event.target instanceof Element && event.target.classList.contains('custom-filter-overlay')) return;

  const target = event.target;
  queueMicrotask(() => {
    if (!isSortUIOpen()) return;
    if (isSortInteractionTarget(target)) return;
    closeSortUI();
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' || !isSortUIOpen()) return;
  closeSortUI();
});

document.addEventListener('section:morphed', () => {
  const panelIdToRestore = persistOpenFilterPanelId;
  persistOpenFilterPanelId = null;

  repatriateQuickFilterPanels();

  if (panelIdToRestore) {
    syncSortInputDisabledState();
    syncQuickPanelsFromURL();
    syncQuickFilterPanelOpenState(panelIdToRestore);
    syncOverlayVisibility();
  } else {
    resetOpenFilterPanels();
    syncSortInputDisabledState();
    syncQuickPanelsFromURL();
    syncOverlayVisibility();
  }

  if (
    (document.querySelector('.custom-filter-panel--mobile-sort.custom-filter-panel--open') &&
      !document.querySelector('.custom-sort-dropdown--mobile details[open]')) ||
    (document.querySelector('.custom-sort-dropdown--desktop .custom-filter-panel--open') &&
      !document.querySelector('.custom-sort-dropdown--desktop details[open]'))
  ) {
    closeSortUI();
  }
});

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('custom-filters-drawer__form')) {
      return;
    }

    event.preventDefault();

    const facetsForm = form.closest('facets-form-component');
    if (facetsForm && typeof facetsForm.updateFilters === 'function') {
      facetsForm.updateFilters();
    }

    // Find the dialog-component and call closeDialog to properly restore scroll
    const dialogComponent = form.closest('dialog-component');
    if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
      dialogComponent.closeDialog();
    }
  },
  true
);

syncSortInputDisabledState();
syncQuickPanelsFromURL();
repatriateQuickFilterPanels();
window.addEventListener('resize', debounce(() => {
  syncSortInputDisabledState();
  repositionOpenFilterPanels();
}, 150));
window.addEventListener('scroll', scheduleRepositionOpenFilterPanels, { capture: true, passive: true });

document.addEventListener('shopify:section:load', () => {
  OverlayManager.hide();
});

// Also handle theme editor events
if (Shopify.designMode) {
  document.addEventListener('shopify:section:reorder', () => {
    OverlayManager.hide();
  });
}
